import { decamelize } from 'humps';
import * as R from 'ramda';
import React from 'react';
import { GeologyType } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import type { KeyParametersPanelProps } from '~/components/key-parameters/key-parameters-panel-v4';
import { KPTitle } from '~/components/key-parameters/key-parameters-panel-v4';
import {
  KPCol,
  KPGrid,
} from '~/components/key-parameters/key-parameters-panel-v4/combined-view';
import { LinkedOtherValue } from '~/components/key-parameters/key-parameters-panel-v4/linked-other-value';
import { WikiPopoverV4 } from '~/components/wiki/wiki-popover-v4';
import type {
  OCAdditionalFields,
  OCAddtlField,
} from '~/utils/modules/outcrop-v4';
import {
  additionalCarbonateFields,
  additionalClasticFields,
  additionalFieldsConfig,
  additionalStructuralFields,
} from '~/utils/modules/outcrop-v4';
import { capitalize } from '~/utils/text';

const defaultFieldLabel = (fieldName: string) =>
  R.pipe(
    () => fieldName,
    value => decamelize(value, { separator: ' ' }),
    R.toLower,
    capitalize,
  )();

function shouldRenderField(value?: string | string[] | null) {
  return !R.isNil(value) && value.length > 0;
}

function getValue(outcrop: OCAdditionalFields, name: keyof OCAdditionalFields) {
  return outcrop[name];
}

type AddtlFieldProps = {
  fieldName: OCAddtlField;
  value: string | string[] | null | undefined;
  leftColMd: number;
  rightColMd: number;
};

function AddtlField({
  fieldName,
  value,
  leftColMd,
  rightColMd,
}: AddtlFieldProps) {
  const config = additionalFieldsConfig[fieldName];

  return (
    <KPGrid key={fieldName}>
      <KPCol span={leftColMd}>
        <span className="space-x-1">
          <KPTitle name={config.label ?? defaultFieldLabel(fieldName)} />
          {config.wikiId && (
            <WikiPopoverV4
              otherWikiId={config.wikiId}
              popoverProps={{ side: 'right', align: 'end' }}
            />
          )}
        </span>
      </KPCol>
      <KPCol span={rightColMd}>
        <LinkedOtherValue fieldName={fieldName}>
          {Array.isArray(value) ? value.join(', ') : value}
        </LinkedOtherValue>
      </KPCol>
    </KPGrid>
  );
}

export function OCAdditionalFieldsDisplay({
  geologyType,
  outcrop,
  keyParameters,
  leftColMd,
  rightColMd,
}: {
  geologyType: GeologyType;
  /** Partial outcrop object with the additional fields selected */
  outcrop: OCAdditionalFields;
  keyParameters: KeyParametersPanelProps['keyParameters'];
  leftColMd: number;
  rightColMd: number;
}) {
  const mapField = (fieldName: OCAddtlField) => {
    const value = getValue(outcrop, fieldName);
    if (!shouldRenderField(value)) return null;

    return (
      <AddtlField
        key={fieldName}
        fieldName={fieldName}
        value={value}
        leftColMd={leftColMd}
        rightColMd={rightColMd}
      />
    );
  };

  let fields: React.ReactNode[] = [];
  if (geologyType === GeologyType.Clastic) {
    fields = additionalClasticFields('outcrop')
      .filter(fieldName => fieldName !== 'netToGross') // NtG is rendered in the main details area
      .map(mapField);
  } else if (geologyType === GeologyType.Carbonate) {
    fields = additionalCarbonateFields().map(mapField);
  } else if (geologyType === GeologyType.Structural) {
    fields = additionalStructuralFields(keyParameters).map(mapField);
  }

  if (!fields.length || R.all(R.isNil, fields)) {
    return null;
  }

  return (
    <div className="mt-4">
      <Heading level={4} className="mb-2">
        Additional Properties
      </Heading>

      {fields}
    </div>
  );
}
